import React, { useEffect, useRef, useState } from "react";
import { IoMdAdd } from "react-icons/io";
import { connect } from "react-redux";
import axios from "../../../axios/axios";
import { Auth, User } from "../../../ts/Auth";
import ReduxState from "../../../ts/Redux";
import { Cell, Column, TableReference } from "../../../ts/UI";
import Button from "../../ui/Button/Button";
import Loading from "../../ui/Loading/Loading";
import Table from "../../ui/Table/Table";
import "./Pouzivatelia.scss";
import { useSelector } from "../../../redux/store";
import { UserRole } from "../../../types/role";
import { useQueryWithPopupErrorHandling } from "../../../providers/utils/hooks";
import { userModel } from "../../../api/models/userModel";

interface Props {}

const Pouzivatelia: React.FC<Props> = ({}: Props) => {
  const auth = useSelector((state) => state.auth);
  const [obchodnici, setObchodnici] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [isAddingNewRow, setIsAddingNewRow] = useState(false);
  const tableRef = useRef<TableReference>(null);

  const { call } = useQueryWithPopupErrorHandling();

  const getObchodnici = async (): Promise<void> => {
    setLoading(true);
    try {
      const req = await axios.get("/user/fetch-obchodnici");

      setObchodnici(req.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const editObchodnik = async (obchodnik: User) => {
    if (auth.user.function === UserRole.Root) {
      call(
        () => userModel.adminEditUserInfo(obchodnik),
        "Úprava uživatela nebola úspešná",
        "Úprava uživatela bola úspešná",
        () => {
          getObchodnici();
        }
      );
    }
  };

  const deleteObchodnik = async (obchodnik: User) => {
    if (auth.user.function === UserRole.Root) {
      call(
        () => userModel.adminRemoveUser(obchodnik._id),
        "Odstránenie uživatela nebolo úspešné",
        "Odstránenie uživatela bolo úspešné",
        () => {
          getObchodnici();
        }
      );
    }
  };

  const addObchodnik = async (obchodnik: User) => {
    if (auth.user.function === UserRole.Root) {
      call(
        () =>
          userModel.adminAddUser({
            email: obchodnik.email,
            firstName: obchodnik.firstName,
            function: obchodnik.function,
            lastName: obchodnik.lastName,
            mobil: obchodnik.mobil,
          }),
        "Pridanie uživatela nebolo úspešné",
        "Pridanie uživatela bolo úspešné",
        () => {
          getObchodnici();
        }
      );
    }
  };

  useEffect(() => {
    getObchodnici();
  }, []);

  const canEdit = auth.user.function === "root";

  const obchodniciColumns: Column[] = [
    { key: "_id", visible: false },
    { key: "firstName", width: "10%", title: "Meno" },
    { key: "lastName", width: "10%", title: "Priezvisko" },
    { key: "email", width: "20%", title: "Email" },
    {
      key: "function",
      width: "20%",
      title: "Funkcia",
      placeholder: "obchodnik nebo root",
    },
    { key: "mobil", width: "20%", title: "Mobil" },
    {
      key: "last_online",
      width: "20%",
      title: "Naposledy prihlásený",
      editable: false,
    },
  ];

  if (canEdit) {
    obchodniciColumns.push({ key: "actions" });
  }

  if (loading) {
    return <Loading text="Získávanie obchodníkov zo servera" />;
  }

  return (
    <div className="pouzivatelia">
      <div className="title-wrapper">POUŽIVATELIA</div>
      {canEdit && (
        <div style={{ marginBottom: 8 }}>
          <Button
            disabled={isAddingNewRow}
            onClick={() => {
              setIsAddingNewRow(true);
              tableRef.current?.addNewRow();
            }}
          >
            <div style={{ display: "flex", alignItems: "center" }}>
              {" "}
              <IoMdAdd
                size={16}
                style={{
                  marginRight: 10,
                }}
              />{" "}
              <div>PRIDAŤ</div>
            </div>
          </Button>
        </div>
      )}
      <Table
        ref={tableRef}
        onNewRowCancel={() => setIsAddingNewRow(false)}
        newRowHandler={(record: User) => {
          addObchodnik(record);
          setIsAddingNewRow(false);
        }}
        isDeletable={(record: Cell[]) => {
          return (
            record.find((cell) => cell.key === "function")?.value !== "root"
          );
        }}
        actions={canEdit}
        editHandler={editObchodnik}
        deleteHandler={deleteObchodnik}
        columns={obchodniciColumns}
        data={obchodnici.map((o) => {
          return {
            ...o,
            _id: o._id,
            email: o.email ? o.email : "",
            firstName: o.firstName,
            lastName: o.lastName,
            function: o.function,
            mobil: o.mobil ? o.mobil : "",
            last_online: o.last_online
              ? new Date(o.last_online).toLocaleDateString() +
                " " +
                new Date(o.last_online).toLocaleTimeString()
              : "Nikdy",
          };
        })}
      />
    </div>
  );
};

export default Pouzivatelia;
